import { TradingStatistics } from '../../services/mypageService';
import './StatisticsChart.css';

interface StatisticsChartProps {
  statistics: TradingStatistics;
  theme: 'light' | 'dark';
}

export const StatisticsChart = ({
  statistics,
  theme,
}: StatisticsChartProps) => {
  const formatNumber = (value: number | null) => {
    const safeValue = value ?? 0;
    return new Intl.NumberFormat('ko-KR', {
      style: 'currency',
      currency: 'KRW',
      maximumFractionDigits: 0,
    }).format(safeValue);
  };

  const formatNumberWithRealPercent = (amount: number | null, rate: number | null) => {
    // amount와 rate가 null일 경우 0으로 처리
    const safeAmount = amount ?? 0;
    const safeRate = rate ?? 0;
    return (
      <span className="stat-value-container">
        <span className="main-value">{formatNumber(safeAmount)}</span>
        <span className="sub-percent">({safeRate.toFixed(2)}%)</span>
      </span>
    );
  };

  const formatNumberWithPercent = (value: number | null) => {
    // value가 null일 경우 0으로 처리
    const safeValue = value ?? 0;
    // 1천만원(시드머니) 기준 퍼센트 계산
    const percent = ((safeValue / 10000000) * 100).toFixed(2);
    return (
      <span className="stat-value-container">
        <span className="main-value">{formatNumber(safeValue)}</span>
        <span className="sub-percent">({percent}%)</span>
      </span>
    );
  };

  // 승률 막대 그래프
  const winRate = statistics.winRate ?? 0;
  const winRateBar = (
    <div className="stat-chart-item">
      <div className="stat-chart-label">승률</div>
      <div className="stat-chart-bar-container">
        <div
          className="stat-chart-bar win-rate-bar"
          style={{ width: `${winRate}%` }}
        >
          <span className="stat-chart-value">
            {winRate.toFixed(1)}%
          </span>
        </div>
      </div>
      <div className="stat-chart-details">
        승리: {statistics.winCount ?? 0}회 / 패배: {statistics.lossCount ?? 0}회
      </div>
    </div>
  );

  // 손익비 막대 그래프
  const profitLossRatio = statistics.profitLossRatio ?? 0;
  const totalProfit = statistics.totalProfit ?? 0;
  const totalLoss = statistics.totalLoss ?? 0;
  const maxPlRatioScale = Math.max(5, Math.ceil(profitLossRatio || 1)); // 최소 5, 그 이상은 올림 처리

  const profitLossRatioBar = (
    <div className="stat-chart-item">
      <div className="stat-chart-label">
        손익비
        <span
          className="info-icon"
          title="총 이익 / 총 손실 비율입니다. 1보다 크면 이익, 작으면 손실입니다."
        >
          ⓘ
        </span>
      </div>
      <div
        className="stat-chart-bar-container"
        title={`총 이익(${formatNumber(
          totalProfit
        )}) ÷ 총 손실(${formatNumber(
          totalLoss
        )}) = ${profitLossRatio.toFixed(
          2
        )} (그래프 기준: ${maxPlRatioScale})`}
      >
        {profitLossRatio > 0 && (
          <div
            className="stat-chart-bar profit-bar"
            style={{
              width: `${
                profitLossRatio === Infinity
                  ? 100
                  : (profitLossRatio / maxPlRatioScale) * 100
              }%`,
            }}
          >
            <span className="stat-chart-value">
              {profitLossRatio === Infinity
                ? '∞'
                : profitLossRatio.toFixed(2)}
            </span>
          </div>
        )}
      </div>
      <div className="stat-chart-details">
        총 수익: {formatNumber(totalProfit)} / 총 손실:{' '}
        {formatNumber(totalLoss)}
      </div>
    </div>
  );

  // 월별 수익 그래프 (최근 12개월, 오래된 순으로 정렬)
  const monthlyData = (statistics.profitByMonth || [])
    .slice(0, 12) // 최대 12개월
    .sort((a, b) => a.month.localeCompare(b.month)) // YYYY-MM 기준 오름차순
    .map(item => ({
      ...item,
      profit: item.profit ?? 0, // null 처리
    }));

  const monthlyProfitChart = (
    <div className="stat-chart-item monthly-chart">
      <div className="stat-chart-label">월별 수익</div>
      <div className="monthly-chart-container">
        {monthlyData.length > 0 ? (
          monthlyData.map((item, index) => {
            const maxProfit = Math.max(
              ...monthlyData.map(m => Math.abs(m.profit)),
              1 // 0으로 나누기 방지
            );
            const height =
              maxProfit > 0 ? (Math.abs(item.profit) / maxProfit) * 100 : 0;
            const isPositive = item.profit >= 0;

            return (
              <div key={index} className="monthly-bar-item">
                <div
                  className={`monthly-bar ${
                    isPositive ? 'positive' : 'negative'
                  }`}
                  style={{ height: `${height}%` }}
                >
                  <span className="monthly-bar-value">
                    {formatNumber(item.profit)}
                  </span>
                </div>
                <div className="monthly-bar-label">
                  {item.month.slice(5)} {/* MM만 표시 */}
                </div>
              </div>
            );
          })
        ) : (
          <div className="no-data">데이터가 없습니다</div>
        )}
      </div>
    </div>
  );

  return (
    <div className={`statistics-chart ${theme}`}>
      <div className="stat-grid">
        <div className="stat-item">
          <div className="stat-item-label">평균 수익</div>
          <div className="stat-item-value positive">
            {formatNumberWithRealPercent(
              statistics.averageProfit,
              statistics.averageProfitRate
            )}
          </div>
        </div>
        <div className="stat-item">
          <div className="stat-item-label">평균 손실</div>
          <div className="stat-item-value negative">
            {formatNumberWithRealPercent(
              statistics.averageLoss,
              statistics.averageLossRate
            )}
          </div>
        </div>
        <div className="stat-item">
          <div className="stat-item-label">최고 수익금</div>
          <div className="stat-item-value positive">
            {formatNumberWithRealPercent(
              statistics.bestProfit,
              statistics.bestProfitRate
            )}
          </div>
        </div>
        <div className="stat-item">
          <div className="stat-item-label">최고 손실금</div>
          <div className="stat-item-value negative">
            {formatNumberWithRealPercent(
              statistics.worstLoss,
              statistics.worstLossRate
            )}
          </div>
        </div>
      </div>

      {winRateBar}
      {profitLossRatioBar}
      {monthlyProfitChart}
    </div>
  );
};
